/**
 * Board backgrounds: plain colour, whiteboard (faint smudges and sheen), chalkboard (dark slate
 * with chalk dust), or an uploaded image. Backgrounds stay fixed to the output frame; the camera
 * moves over the drawings, not the surface. Textures are generated from a fixed seed, so preview and
 * render produce identical pixels.
 *
 * Images are separated from their white background on import, so they composite onto any
 * background; dark line art reads poorly on the chalkboard, which suits light-coloured drawings.
 */

import { makeCanvas, type Ctx2D } from "./canvas";
import { rng } from "./math";
import type { Background } from "./project.gen";

const cache = new Map<string, OffscreenCanvas>();

export const CHALKBOARD = "#2e3b35";

function texture(kind: "whiteboard" | "chalkboard", base: string, W: number, H: number): OffscreenCanvas {
  const key = `${kind}|${base}|${W}x${H}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const { canvas, ctx } = makeCanvas(W, H);
  const rand = rng(kind === "whiteboard" ? 7 : 11);
  ctx.fillStyle = kind === "chalkboard" ? (base === "#ffffff" ? CHALKBOARD : base) : base;
  ctx.fillRect(0, 0, W, H);
  const s = W / 1920;
  // broad soft blotches (old wiping marks / uneven slate)
  for (let i = 0; i < 26; i++) {
    const x = rand() * W;
    const y = rand() * H;
    const r = (120 + rand() * 380) * s;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    const a = kind === "whiteboard" ? 0.012 + rand() * 0.018 : 0.04 + rand() * 0.05;
    g.addColorStop(0, kind === "whiteboard" ? `rgba(120,130,140,${a})` : `rgba(255,255,255,${a})`);
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
  }
  // fine grain / chalk dust
  const n = kind === "whiteboard" ? 2500 : 9000;
  for (let i = 0; i < n; i++) {
    const a = kind === "whiteboard" ? 0.035 * rand() : 0.08 * rand();
    ctx.fillStyle = kind === "whiteboard" ? `rgba(90,100,110,${a})` : `rgba(235,240,235,${a})`;
    ctx.fillRect(rand() * W, rand() * H, (0.6 + rand() * 1.4) * s, (0.6 + rand() * 1.4) * s);
  }
  if (kind === "whiteboard") {
    // a faint diagonal sheen from the room light
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "rgba(255,255,255,0)");
    g.addColorStop(0.45, "rgba(255,255,255,0.18)");
    g.addColorStop(0.55, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    // slight vignette toward the frame edges
    const v = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.45, W / 2, H / 2, Math.max(W, H) * 0.75);
    v.addColorStop(0, "rgba(0,0,0,0)");
    v.addColorStop(1, "rgba(60,70,80,0.06)");
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, W, H);
  }
  if (cache.size > 8) cache.clear();
  cache.set(key, canvas);
  return canvas;
}

/** Paint the background into the whole output (identity transform). */
export function drawBackground(ctx: Ctx2D, bg: Background, W: number, H: number, image?: CanvasImageSource & { width: number; height: number }): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  if (bg.kind === "whiteboard" || bg.kind === "chalkboard") {
    ctx.drawImage(texture(bg.kind, bg.color, W, H), 0, 0);
    return;
  }
  ctx.fillStyle = bg.color;
  ctx.fillRect(0, 0, W, H);
  if (bg.kind === "image" && image) {
    // cover the frame, centred
    const k = Math.max(W / image.width, H / image.height);
    const w = image.width * k;
    const h = image.height * k;
    ctx.drawImage(image, (W - w) / 2, (H - h) / 2, w, h);
  }
}
